import React, { FC, ReactNode, useMemo } from 'react';
import { Box, Divider, Paper, Stack, Typography, type TypographyVariant } from '@mui/material';

import HiddenItemCounter from '@/components/HiddenItemCounter/HiddenItemCounter';
import typography from '@/theme/typography';

interface TextItemsDisplayProps {
  items: string[];
  isArchived?: boolean;
  variant?: TypographyVariant;
  visibleItemsCount: number;
  fallback?: string | ReactNode;
}

const TextItemsDisplay: FC<TextItemsDisplayProps> = ({ variant, items, isArchived, visibleItemsCount, fallback }) => {
  const visibleItems = useMemo(() => items.slice(0, visibleItemsCount), [items, visibleItemsCount]);
  const hiddenItems = useMemo(() => items.slice(visibleItemsCount), [items, visibleItemsCount]);

  if (fallback && !visibleItems.length) return fallback;

  return (
    <Box display="flex" alignItems="center">
      <Stack direction="row">
        {visibleItems.map((item, index) => (
          <React.Fragment key={index}>
            <Typography variant={variant}>{item}</Typography>
            {index < visibleItems.length - 1 && (
              <Divider orientation="vertical" flexItem sx={{ mx: 1, borderColor: 'grey.500' }} />
            )}
          </React.Fragment>
        ))}
      </Stack>
      <HiddenItemCounter hiddenItemsCount={hiddenItems.length} isDisabled={isArchived}>
        <Paper
          sx={{
            position: 'relative',
            minWidth: '87px',
            p: 1,
            '& .MuiTypography-root': { color: 'text.primary', ...typography.body3 }
          }}
        >
          <Stack maxHeight="200px" overflow="auto" p={1}>
            <Stack gap={1}>
              {hiddenItems.map(label => (
                <Typography key={label}>{label}</Typography>
              ))}
            </Stack>
          </Stack>
        </Paper>
      </HiddenItemCounter>
    </Box>
  );
};

export default TextItemsDisplay;
